import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { FiCopy } from "react-icons/fi";
import RiskPill from "../buttons/RiskPill";
import DeclineButton from "../buttons/DeclineButton";
import PrimaryButton from "../buttons/PrimaryButton";
import {
  FailedStatusOption,
  PassedStatusOption,
} from "../buttons/PassFailStatusOption";
import { ReactComponent as FlagIcon } from "../../../../assets/icons/financialPromotions/flag.svg";
import "../../../../css/Marketing/FinancialPromotions/Buttons/FlagRatingPills.css";
import RatePill from "../buttons/RatePill";

export const ATTEMPTS_SUBCOL = "attempts";

export const safeJson = (value) => {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return String(value);
  }
};

export const formatDateTime = (value) => {
  const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getLevelColor = (level) => {
  switch ((level || "").toUpperCase()) {
    case "HIGH":
      return "#dc2626";
    case "MED":
      return "#f59e0b";
    case "LOW":
      return "#16a34a";
    default:
      return "#64748b";
  }
};

export const getStatusColor = (status) => {
  switch ((status || "").toLowerCase()) {
    case "awaiting_review":
      return "#f59e0b";
    case "processing":
      return "#0ea5e9";
    case "completed":
      return "#16a34a";
    case "failed":
      return "#dc2626";
    default:
      return "#64748b";
  }
};

const normalizeConfidence = (review) => {
  const raw =
    review?.accuracyConfidence ??
    review?.confidence ??
    review?.inferred?.confidence ??
    0;

  const num = Number(raw);
  if (!Number.isFinite(num)) return 0;
  if (num <= 1) return Math.round(Math.max(0, Math.min(1, num)) * 100);
  return Math.round(Math.max(0, Math.min(100, num)));
};

export const normalizeFlags = (review) => {
  const rawFlags =
    review?.flags ||
    review?.complianceIssues ||
    review?.issues ||
    review?.result?.flags ||
    [];

  if (!Array.isArray(rawFlags)) return [];

  const toSeverityTone = (value) => {
    const normalized = String(value || "").toUpperCase();
    if (normalized.includes("HIGH")) return "high";
    if (normalized.includes("MED")) return "medium";
    if (normalized.includes("LOW")) return "low";
    if (normalized.includes("PROCESS")) return "processing";
    return "unknown";
  };

  const formatLabel = (value, fallback) =>
    String(value || fallback || "")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  return rawFlags.map((flag, index) => ({
    id: flag?.id || flag?.key || `flag-${index}`,
    type: formatLabel(flag?.type || flag?.category, "Issue"),
    severity: formatLabel(flag?.severity || review?.risk, "Unknown"),
    severityTone: toSeverityTone(flag?.severity || review?.risk),
    source:
      flag?.sourceFile || flag?.source || flag?.context || flag?.location || "",
    snippet: flag?.snippet || flag?.foundSnippet || flag?.text || "",
    reason: flag?.reason || flag?.explanation || "",
    fix: flag?.fix || flag?.suggestion || flag?.recommendation || "",
  }));
};

const normalizeReview = (review) => {
  const defaultApprovalConditions = [
    "Approval is valid for a 12 month period only until the expiry date shown above.",
    "This ensures items intended for long term use remain compliant with any changes to regulation.",
    "Please be aware that any future changes to standards may impact on future feedback to clients from pre-approval.",
    "The item has been approved in its current state and no changes should be made without seeking further pre-approval.",
    "The item approved is from a regulatory perspective only and does not cover copyright infringement laws.",
  ];

  const risk = String(
    review?.risk ||
      review?.riskDetected ||
      review?.riskLevel ||
      review?.level ||
      "UNKNOWN",
  ).toUpperCase();

  const sources =
    review?.sources ||
    review?.result?.sources ||
    review?.extraction?.sources ||
    [];

  const flags = normalizeFlags(review);

  const parsedApprovalConditions = Array.isArray(review?.approvalConditions)
    ? review.approvalConditions
        .map((item) =>
          String(item || "")
            .replace(/^\s*[•\-*]\s*/, "")
            .trim(),
        )
        .filter(Boolean)
    : typeof review?.approvalConditions === "string"
      ? review.approvalConditions
          .split("\n")
          .map((line) => line.replace(/^\s*[•\-*]\s*/, "").trim())
          .filter(Boolean)
      : [];

  const suggestedRewrite =
    review?.suggestedRewrite ||
    review?.suggested_rewrite ||
    review?.rewrite ||
    review?.rewrittenText ||
    review?.recommendedRewrite ||
    review?.result?.suggestedRewrite ||
    review?.result?.rewrite ||
    "";

  return {
    risk,
    confidence: normalizeConfidence(review),
    summary: review?.summary || review?.overview || "No summary available.",
    flags,
    decision:
      review?.decision ||
      review?.result?.decision ||
      "Staff Review and Feedback",
    staffReviewText:
      review?.staffReviewText || review?.staffReview || review?.feedback || "",
    approvalConditions:
      parsedApprovalConditions.length > 0
        ? parsedApprovalConditions
        : defaultApprovalConditions,
    suggestedRewrite: String(suggestedRewrite || "").trim(),
    sources: Array.isArray(sources) ? sources : [],
  };
};

export const MediaReviewCard = ({
  review,
  hasAiAnalysis = true,
  showFlags = true,
  attachments = [],
  attemptId = "",
  fileReviewDecisions = {},
  savingFileDecisions = {},
  flagRatings = {},
  savingFlagRatings = {},
  buildFlagDecisionKey,
  isMediaDecisionEditable = true,
  getAttachmentDecisionKey,
  onOpenAttachment,
  onSaveAttachmentDecision,
  onSaveFlagRating,
  showRequestChanges = true,
  showDecisionAndFeedback = true,
  showApprovalConditions = true,
  isReadOnly = false,
  isApprovalEditable = true,
  isStaffReviewEditable = true,
  isFinalDecisionEditable = true,
  onSaveReview,
  isSaving = false,
}) => {
  const normalized = normalizeReview(review || {});
  const [confirmDecision, setConfirmDecision] = useState(null);
  // "approved" | "declined" | "requested_changes" | null
  const {
    risk,
    confidence,
    summary,
    flags,
    decision,
    approvalConditions,
    staffReviewText,
    suggestedRewrite,
  } = normalized;
  const riskColor = getLevelColor(risk);
  const riskLabel = risk
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const initialApprovalText = useMemo(
    () => approvalConditions.map((condition) => `• ${condition}`).join("\n"),
    [approvalConditions],
  );

  const [approvalText, setApprovalText] = useState(initialApprovalText);
  const [staffReviewInput, setStaffReviewInput] = useState(
    staffReviewText || "",
  );
  const [isRewriteCopied, setIsRewriteCopied] = useState(false);

  useEffect(() => {
    setApprovalText(initialApprovalText);
  }, [initialApprovalText]);

  useEffect(() => {
    setStaffReviewInput(staffReviewText || "");
  }, [staffReviewText]);

  const toApprovalConditionsArray = (text) =>
    String(text || "")
      .split("\n")
      .map((line) => line.replace(/^\s*[•\-*]\s*/, "").trim())
      .filter(Boolean);

  const handleSaveDecision = async (nextDecision) => {
    if (!onSaveReview) return;

    await onSaveReview({
      decision: nextDecision,
      staffReviewText: staffReviewInput,
      approvalConditions: toApprovalConditionsArray(approvalText),
      approvalConditionsText: approvalText,
    });

    setConfirmDecision(null);
  };

  const handleCopySuggestedRewrite = async () => {
    if (!suggestedRewrite) return;

    try {
      await navigator.clipboard.writeText(suggestedRewrite);
      setIsRewriteCopied(true);
      setTimeout(() => setIsRewriteCopied(false), 1500);
    } catch {
      setIsRewriteCopied(false);
    }
  };

  //   if (!review) return null;

  const resolveAttachmentDecisionKey = (attachment) => {
    if (typeof getAttachmentDecisionKey === "function") {
      return getAttachmentDecisionKey(attachment);
    }

    return String(
      attachment?.storagePath ||
        attachment?.downloadURL ||
        attachment?.name ||
        "attachment",
    )
      .trim()
      .toLowerCase();
  };

  const isFlagForAttachment = (flag, attachmentNameKey) => {
    const source = String(flag?.source || "")
      .trim()
      .toLowerCase();
    return (
      source === attachmentNameKey ||
      source.endsWith(`/${attachmentNameKey}`) ||
      source.endsWith(`\\${attachmentNameKey}`)
    );
  };

  return (
    <div
      className={`admin-review-card fp-ai-review ${isReadOnly ? "is-readonly" : ""}`}
    >
      <div className="fp-ai-review-title FP_Subtitle">AI Review</div>
      <div className="FP_Content_Semibold">
        Please Note: The flags and suggested rewrite will not be visible to the
        adviser. Please copy any appropriate content into the notes section
        before sending a decision.
      </div>

      {hasAiAnalysis && (
        <>
          <div className="fp-ai-review-subtitle FP_Body">Risk Detected</div>
          <div className="fp-ai-review-toprow">
            {/* <span className={`fp-ai-pill fp-ai-pill--${risk.toLowerCase()}`}>
              {riskLabel}
                  </span> */}
            <RiskPill risk={risk} className="fp-ai-risk-pill--summary" />
            <span className="fp-ai-pill fp-ai-pill--count FP_Content">
              {flags.length} Compliance Issues
            </span>
            <div className="fp-ai-confidence-inline">
              <div className="fp-ai-confidence-head FP_Content">
                <span>Accuracy Confidence</span>
                <span>{confidence}%</span>
              </div>

              <div className="fp-ai-confidence-track">
                <div
                  className="fp-ai-confidence-fill"
                  style={{
                    width: `${confidence}%`,
                    backgroundColor: riskColor,
                  }}
                />
              </div>
            </div>
          </div>
          {flags.length > 0 && (
            <>
              <div className="fp-ai-flags-title FP_Content">
                Flags ({flags.length})
              </div>
              <div className="fp-ai-flags-note FP_Content">
                Please Note: The following suggestions will not be visible to
                the adviser. Please copy any appropriate fixes into notes before
                sending a decision.
              </div>

              {flags.map((flag, index) => {
                const flagDecisionKey =
                  typeof buildFlagDecisionKey === "function"
                    ? buildFlagDecisionKey(attemptId, flag)
                    : `${attemptId || "attempt"}::${flag?.id || index}::${String(
                        flag?.source || "",
                      )
                        .trim()
                        .toLowerCase()}::${String(flag?.type || "")
                        .trim()
                        .toLowerCase()}::${String(flag?.snippet || "")
                        .trim()
                        .toLowerCase()}`;

                const flagRating = flagRatings?.[flagDecisionKey] || {};
                const ratingValue = String(
                  flagRating?.rating || "",
                ).toLowerCase();
                const reviewedByEmail = String(
                  flagRating?.reviewedByEmail || "",
                ).trim();
                const reviewedAt = flagRating?.reviewedAt;
                const isSavingFlag = Boolean(
                  savingFlagRatings?.[flagDecisionKey],
                );

                return (
                  <div
                    key={flag.id || `flag-${index}`}
                    className={`fp-ai-flag-card FP_Content fp-ai-flag-card--${flag.severityTone}`}
                  >
                    <div
                      className={`fp-ai-flag-header FP_Content fp-ai-flag-header--${flag.severityTone}`}
                    >
                      <FlagIcon className="fp-ai-flag-icon" />
                      <span className="fp-ai-flag-type FP_Content">
                        {flag.type}
                      </span>
                    </div>

                    {flag.source && (
                      <div className="fp-ai-flag-field FP_Content">
                        <div className="FP_Content_Semibold">Source</div>
                        <div
                          className="FP_Content"
                          style={{ color: "var(--Grey70)" }}
                        >
                          {flag.source || "-"}
                        </div>
                      </div>
                    )}

                    {flag.snippet && (
                      <div className="fp-ai-flag-field FP_Content">
                        <div className="FP_Content_Semibold">Snippet</div>
                        <div
                          className="FP_Content"
                          style={{ color: "var(--Grey70)" }}
                        >
                          {flag.snippet || "-"}
                        </div>
                      </div>
                    )}

                    {flag.reason && (
                      <div className="fp-ai-flag-field FP_Content">
                        <div className="FP_Content_Semibold">Reason</div>
                        <div className="FP_Content">{flag.reason || "-"}</div>
                      </div>
                    )}

                    {flag.fix && (
                      <div className="fp-ai-flag-field fp-ai-flag-fix">
                        <div className="FP_Content_Semibold">Fix</div>
                        <div
                          className="FP_Content"
                          style={{ color: "var(--Brand1-PenBlue)" }}
                        >
                          {flag.fix || "-"}
                        </div>
                      </div>
                    )}

                    <div className="fp-ai-flag-field FP_Content">
                      <div className="FP_Content_Semibold">Rating</div>
                      <div className="fp-flag-rating-actions fp-flag-rating-pills">
                        <RatePill
                          tone="correct"
                          label="Correct"
                          selected={ratingValue === "correct"}
                          disabled={isReadOnly || isSavingFlag}
                          onClick={() =>
                            onSaveFlagRating?.(flag, "correct", flagDecisionKey)
                          }
                        />

                        <RatePill
                          tone="partly-correct"
                          label="Partly Correct"
                          selected={ratingValue === "partly_correct"}
                          disabled={isReadOnly || isSavingFlag}
                          onClick={() =>
                            onSaveFlagRating?.(
                              flag,
                              "partly_correct",
                              flagDecisionKey,
                            )
                          }
                        />

                        <RatePill
                          tone="incorrect"
                          label="Incorrect"
                          selected={ratingValue === "incorrect"}
                          disabled={isReadOnly || isSavingFlag}
                          onClick={() =>
                            onSaveFlagRating?.(
                              flag,
                              "incorrect",
                              flagDecisionKey,
                            )
                          }
                        />
                        <RatePill
                          tone="not_relevant"
                          label="Not Relevant"
                          selected={ratingValue === "not_relevant"}
                          disabled={isReadOnly || isSavingFlag}
                          onClick={() =>
                            onSaveFlagRating?.(
                              flag,
                              "not_relevant",
                              flagDecisionKey,
                            )
                          }
                        />
                      </div>

                      {(ratingValue || reviewedByEmail || reviewedAt) && (
                        <div className="fp-flag-rating-meta FP_Help_Text">
                          {`${
                            ratingValue
                              ? `Marked as ${
                                  ratingValue === "partly_correct"
                                    ? "Partly Correct"
                                    : ratingValue === "not_relevant"
                                      ? "Not Relevant"
                                      : ratingValue.charAt(0).toUpperCase() +
                                        ratingValue.slice(1)
                                }`
                              : "Marked"
                          }${reviewedByEmail ? ` by ${reviewedByEmail}` : ""}${
                            reviewedAt
                              ? ` on ${formatDateTime(reviewedAt)}`
                              : ""
                          }.`}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          <div className="fp-ai-review-subtitle FP_Content">Summary</div>
          <div className="fp-ai-summary FP_Content">{summary}</div>
          {/* 
          {attachments.length > 0 && (
            <>
              <div className="FP_Content_Semibold">Attachments</div>
              <div className="fp-admin-files-list">
                {attachments.map((attachment, attachmentIndex) => {
                  const attachmentNameKey = String(attachment?.name || "")
                    .trim()
                    .toLowerCase();
                  const matchedFlags = flags.filter((flag) =>
                    isFlagForAttachment(flag, attachmentNameKey),
                  );
                  const decisionKey = resolveAttachmentDecisionKey(attachment);
                  const fileDecision = fileReviewDecisions?.[decisionKey];
                  const fileDecisionValue = String(
                    fileDecision?.decision || "",
                  ).toLowerCase();
                  const fileDecisionReviewerEmail = String(
                    fileDecision?.reviewedByEmail || "",
                  )
                    .trim()
                    .toLowerCase();
                  const fileDecisionLabel =
                    fileDecisionValue === "approved"
                      ? "Passed"
                      : fileDecisionValue === "declined"
                        ? "Failed"
                        : "";
                  const saveKey = attemptId
                    ? `${attemptId}:${decisionKey}`
                    : decisionKey;
                  const isSavingFile = Boolean(savingFileDecisions?.[saveKey]);
                  const canOpen = Boolean(
                    attachment?.downloadURL || attachment?.storagePath,
                  );

                  return (
                    <div
                      key={`${attemptId || "attempt"}-file-${attachmentIndex}`}
                      className="fp-admin-file-group"
                    >
                      <div className="fp-admin-file-row">
                        <button
                          type="button"
                          className="fp-admin-file-link FP_Content"
                          disabled={!canOpen}
                          onClick={() =>
                            canOpen && onOpenAttachment
                              ? onOpenAttachment(attachment)
                              : null
                          }
                        >
                          {attachment.name}
                          {canOpen ? "" : " (Unavailable)"}
                        </button>

                        <div className="fp-admin-decision-stack">
                          <div className="fp-admin-file-actions">
                            <PassedStatusOption
                              selected={fileDecisionValue === "approved"}
                              disabled={
                                !isMediaDecisionEditable || isSavingFile
                              }
                              onClick={() =>
                                onSaveAttachmentDecision
                                  ? onSaveAttachmentDecision(
                                      attachment,
                                      "approved",
                                    )
                                  : null
                              }
                            />
                            <FailedStatusOption
                              selected={fileDecisionValue === "declined"}
                              disabled={
                                !isMediaDecisionEditable || isSavingFile
                              }
                              onClick={() =>
                                onSaveAttachmentDecision
                                  ? onSaveAttachmentDecision(
                                      attachment,
                                      "declined",
                                    )
                                  : null
                              }
                            />
                          </div>
                          {fileDecisionLabel && fileDecisionReviewerEmail && (
                            <div className="FP_Content fp-admin-decision-meta">
                              {`${fileDecisionReviewerEmail}`}
                            </div>
                          )}
                        </div>
                      </div>

                      {matchedFlags.length > 0 && (
                        <div className="fp-admin-file-flags FP_Content">
                          {matchedFlags.map((flag) => (
                            <div
                              key={`${attemptId || "attempt"}-${attachmentIndex}-${flag.id}`}
                              className={`fp-ai-flag-card fp-ai-flag-card--${flag.severityTone}`}
                            >
                              <div
                                className={`fp-ai-flag-header fp-ai-flag-header--${flag.severityTone}`}
                              >
                                <span className="fp-ai-flag-type">
                                  {flag.type}
                                </span>
                              </div>
                              <div className="fp-ai-flag-field">
                                <strong>Snippet</strong>
                                <div>{flag.snippet || "-"}</div>
                              </div>
                              <div className="fp-ai-flag-field">
                                <strong>Reason</strong>
                                <div>{flag.reason || "-"}</div>
                              </div>
                              <div className="fp-ai-flag-field fp-ai-flag-fix">
                                <strong>Fix</strong>
                                <div>{flag.fix || "-"}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {flags.length > 0 &&
                flags.some((flag) => {
                  const flagSource = String(flag?.source || "")
                    .trim()
                    .toLowerCase();
                  return !attachments.some((attachment) => {
                    const attachmentNameKey = String(attachment?.name || "")
                      .trim()
                      .toLowerCase();
                    return isFlagForAttachment(
                      { source: flagSource },
                      attachmentNameKey,
                    );
                  });
                }) && (
                  <div className="fp-admin-file-flags" style={{ marginTop: 8 }}>
                    <div className="FP_Content_Semibold">Other Flags</div>
                    {flags
                      .filter((flag) => {
                        const flagSource = String(flag?.source || "")
                          .trim()
                          .toLowerCase();
                        return !attachments.some((attachment) => {
                          const attachmentNameKey = String(
                            attachment?.name || "",
                          )
                            .trim()
                            .toLowerCase();
                          return isFlagForAttachment(
                            { source: flagSource },
                            attachmentNameKey,
                          );
                        });
                      })
                      .map((flag) => (
                        <div
                          key={`${attemptId || "attempt"}-unmatched-${flag.id}`}
                          className={`fp-ai-flag-card fp-ai-flag-card--${flag.severityTone}`}
                        >
                          <div
                            className={`fp-ai-flag-header fp-ai-flag-header--${flag.severityTone}`}
                          >
                            <span className="fp-ai-flag-type">{flag.type}</span>
                          </div>
                          <div className="fp-ai-flag-field">
                            <strong>Source</strong>
                            <div>{flag.source || "-"}</div>
                          </div>
                          <div className="fp-ai-flag-field">
                            <strong>Snippet</strong>
                            <div>{flag.snippet || "-"}</div>
                          </div>
                          <div className="fp-ai-flag-field">
                            <strong>Reason</strong>
                            <div>{flag.reason || "-"}</div>
                          </div>
                          <div className="fp-ai-flag-field fp-ai-flag-fix">
                            <strong>Fix</strong>
                            <div>{flag.fix || "-"}</div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
            </>
          )} */}

          {/* {flags.length > 0 && !attachments.length && (
            <>
              <div className="fp-ai-flags-title FP_Content">
                Flags ({flags.length})
              </div>
              <div className="fp-ai-flags-note FP_Content">
                Please Note: The following suggestions will not be visible to
                the adviser. Please copy any appropriate fixes into notes before
                sending a decision.
              </div>

              {flags.map((flag, index) => {
                const flagDecisionKey =
                  typeof buildFlagDecisionKey === "function"
                    ? buildFlagDecisionKey(attemptId, flag)
                    : `${attemptId || "attempt"}::${flag?.id || index}::${String(
                        flag?.source || "",
                      )
                        .trim()
                        .toLowerCase()}::${String(flag?.type || "")
                        .trim()
                        .toLowerCase()}::${String(flag?.snippet || "")
                        .trim()
                        .toLowerCase()}`;

                const flagRating = flagRatings?.[flagDecisionKey] || {};
                const ratingValue = String(
                  flagRating?.rating || "",
                ).toLowerCase();
                const reviewedByEmail = String(
                  flagRating?.reviewedByEmail || "",
                ).trim();
                const reviewedAt = flagRating?.reviewedAt;
                const isSavingFlag = Boolean(
                  savingFlagRatings?.[flagDecisionKey],
                );

                return (
                  <div
                    key={flag.id || `flag-${index}`}
                    className={`fp-ai-flag-card FP_Content fp-ai-flag-card--${flag.severityTone}`}
                  >
                    <div
                      className={`fp-ai-flag-header FP_Content fp-ai-flag-header--${flag.severityTone}`}
                    >
                      <FlagIcon className="fp-ai-flag-icon" />
                      <span className="fp-ai-flag-type FP_Content">
                        {flag.type}
                      </span>
                    </div>

                    {flag.source && (
                      <div className="fp-ai-flag-field FP_Content">
                        <div className="FP_Content_Semibold">Source</div>
                        <div
                          className="FP_Content"
                          style={{ color: "var(--Grey70)" }}
                        >
                          {flag.source || "-"}
                        </div>
                      </div>
                    )}

                    {flag.snippet && (
                      <div className="fp-ai-flag-field FP_Content">
                        <div className="FP_Content_Semibold">Snippet</div>
                        <div
                          className="FP_Content"
                          style={{ color: "var(--Grey70)" }}
                        >
                          {flag.snippet || "-"}
                        </div>
                      </div>
                    )}

                    {flag.reason && (
                      <div className="fp-ai-flag-field FP_Content">
                        <div className="FP_Content_Semibold">Reason</div>
                        <div className="FP_Content">{flag.reason || "-"}</div>
                      </div>
                    )}

                    {flag.fix && (
                      <div className="fp-ai-flag-field fp-ai-flag-fix">
                        <div className="FP_Content_Semibold">Fix</div>
                        <div
                          className="FP_Content"
                          style={{ color: "var(--Brand1-PenBlue)" }}
                        >
                          {flag.fix || "-"}
                        </div>
                      </div>
                    )}

                    <div className="fp-ai-flag-field FP_Content">
                      <div className="FP_Content_Semibold">Rating</div>
                      <div className="fp-flag-rating-actions fp-flag-rating-pills">
                        <RatePill
                          tone="correct"
                          label="Correct"
                          selected={ratingValue === "correct"}
                          disabled={isReadOnly || isSavingFlag}
                          onClick={() =>
                            onSaveFlagRating?.(flag, "correct", flagDecisionKey)
                          }
                        />

                        <RatePill
                          tone="partly-correct"
                          label="Partly Correct"
                          selected={ratingValue === "partly_correct"}
                          disabled={isReadOnly || isSavingFlag}
                          onClick={() =>
                            onSaveFlagRating?.(
                              flag,
                              "partly_correct",
                              flagDecisionKey,
                            )
                          }
                        />

                        <RatePill
                          tone="incorrect"
                          label="Incorrect"
                          selected={ratingValue === "incorrect"}
                          disabled={isReadOnly || isSavingFlag}
                          onClick={() =>
                            onSaveFlagRating?.(
                              flag,
                              "incorrect",
                              flagDecisionKey,
                            )
                          }
                        />
                      </div>

                      {(ratingValue || reviewedByEmail || reviewedAt) && (
                        <div className="fp-flag-rating-meta FP_Help_Text ">
                          {`${
                            ratingValue
                              ? `Marked as ${
                                  ratingValue === "partly_correct"
                                    ? "Partly Correct"
                                    : ratingValue.charAt(0).toUpperCase() +
                                      ratingValue.slice(1)
                                }`
                              : "Marked"
                          }${reviewedByEmail ? ` by ${reviewedByEmail}` : ""}${reviewedAt ? ` on ${formatDateTime(reviewedAt)}` : ""}.`}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )} */}

          {suggestedRewrite ? (
            <>
              <div className="fp-ai-review-subtitle-row FP_Content">
                <div className="fp-ai-review-subtitle">Suggested rewrite</div>
                <button
                  type="button"
                  className={`fp-ai-rewrite-copy-btn ${isRewriteCopied ? "is-copied" : ""}`}
                  onClick={handleCopySuggestedRewrite}
                  aria-label="Copy suggested rewrite"
                  title={isRewriteCopied ? "Copied" : "Copy suggested rewrite"}
                >
                  <FiCopy aria-hidden="true" />
                  <span>{isRewriteCopied ? "Copied" : "Copy"}</span>
                </button>
              </div>
              <div className="admin-rewrite FP_Content">{suggestedRewrite}</div>
            </>
          ) : null}
        </>
      )}

      {attachments.length > 0 && (
        <>
          <div className="FP_Content_Semibold">Attachments</div>
          <div className="fp-admin-files-list">
            {attachments.map((attachment, attachmentIndex) => {
              const decisionKey = resolveAttachmentDecisionKey(attachment);
              const fileDecision = fileReviewDecisions?.[decisionKey];
              const fileDecisionValue = String(
                fileDecision?.decision || "",
              ).toLowerCase();
              const fileDecisionReviewerEmail = String(
                fileDecision?.reviewedByEmail || "",
              )
                .trim()
                .toLowerCase();
              const fileDecisionLabel =
                fileDecisionValue === "approved"
                  ? "Passed"
                  : fileDecisionValue === "declined"
                    ? "Failed"
                    : "";
              const saveKey = attemptId
                ? `${attemptId}:${decisionKey}`
                : decisionKey;
              const isSavingFile = Boolean(savingFileDecisions?.[saveKey]);
              const canOpen = Boolean(
                attachment?.downloadURL || attachment?.storagePath,
              );

              return (
                <div
                  key={`${attemptId || "attempt"}-file-${attachmentIndex}`}
                  className="fp-admin-file-group"
                >
                  <div className="fp-admin-file-row">
                    <button
                      type="button"
                      className="fp-admin-file-link FP_Content"
                      disabled={!canOpen}
                      onClick={() =>
                        canOpen && onOpenAttachment
                          ? onOpenAttachment(attachment)
                          : null
                      }
                    >
                      {attachment.name}
                      {canOpen ? "" : " (Unavailable)"}
                    </button>

                    <div className="fp-admin-decision-stack">
                      <div className="fp-admin-file-actions">
                        <PassedStatusOption
                          selected={fileDecisionValue === "approved"}
                          disabled={!isMediaDecisionEditable || isSavingFile}
                          onClick={() =>
                            onSaveAttachmentDecision
                              ? onSaveAttachmentDecision(attachment, "approved")
                              : null
                          }
                        />
                        <FailedStatusOption
                          selected={fileDecisionValue === "declined"}
                          disabled={!isMediaDecisionEditable || isSavingFile}
                          onClick={() =>
                            onSaveAttachmentDecision
                              ? onSaveAttachmentDecision(attachment, "declined")
                              : null
                          }
                        />
                      </div>

                      {fileDecisionLabel && fileDecisionReviewerEmail && (
                        <div className="FP_Content fp-admin-decision-meta">
                          {fileDecisionReviewerEmail}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {!showApprovalConditions && (
        <>
          <div className="fp-ai-review-subtitle FP_Content">Decision</div>
          {/* <div className="fp-ai-decision-label">{decision}</div> */}
          <div className="fp-ai-feedback-title FP_Content">
            Staff Review and Feedback
          </div>
          <div className="fp-ai-feedback-help FP_Content">
            This message will be sent to the adviser as a part of the feedback
            loop.
          </div>
          <textarea
            className={`fp-ai-feedback-input FP_Content ${isStaffReviewEditable ? "" : "is-readonly"}`}
            rows={3}
            placeholder="Enter notes to send to the adviser"
            value={staffReviewInput}
            onChange={(event) => setStaffReviewInput(event.target.value)}
            readOnly={!isStaffReviewEditable}
          />
          <div className="fp-ai-feedback-actions">
            {/* <DeclineButton
              disabled={!isFinalDecisionEditable || isSaving}
              onClick={() => handleSaveDecision("declined")}
            /> */}
            {showRequestChanges && (
              <PrimaryButton
                className="fp-ai-btn fp-ai-btn--request"
                disabled={!isStaffReviewEditable || isSaving}
                // onClick={() => handleSaveDecision("requested_changes")}
                onClick={() => setConfirmDecision("requested_changes")}
                label="Request Changes"
              />
            )}
          </div>
        </>
      )}

      {showApprovalConditions && (
        <>
          <div className="fp-ai-feedback-title FP_Subtitle">
            Approval Conditions
          </div>
          <div className="fp-ai-approval-note FP_Content_Semibold">
            Please Note: Approval conditions will only be sent if decision is
            approved. It will not be sent if changes are requested or if
            submission is declined. Please only edit the conditions if there are
            minor changes that can be approved without requiring changes.
          </div>
          <div className="fp-ai-feedback-title FP_Subtitle">Notes</div>
          <textarea
            className={`fp-ai-feedback-input FP_Content ${isStaffReviewEditable ? "" : "is-readonly"}`}
            rows={3}
            placeholder="Enter notes to send to the adviser"
            value={staffReviewInput}
            onChange={(event) => setStaffReviewInput(event.target.value)}
            readOnly={!isStaffReviewEditable}
          />
          <textarea
            className={`fp-ai-approval-input FP_Content ${isApprovalEditable ? "" : "is-readonly"}`}
            rows={7}
            value={approvalText}
            readOnly={!isApprovalEditable}
            onChange={(event) => setApprovalText(event.target.value)}
          />
          <div className="fp-ai-final-actions">
            <DeclineButton
              disabled={!isFinalDecisionEditable || isSaving}
              // onClick={() => handleSaveDecision("declined")}
              onClick={() => setConfirmDecision("declined")}
            />
            <PrimaryButton
              className="fp-ai-btn fp-ai-btn--request"
              disabled={!isFinalDecisionEditable || isSaving}
              // onClick={() => handleSaveDecision("approved")}
              onClick={() => setConfirmDecision("approved")}
              label="Approve"
            />
          </div>
        </>
      )}
      {confirmDecision && (
        <div className="fp-confirm-overlay">
          <div className="fp-confirm-modal">
            <div className="fp-confirm-title FP_Subtitle">
              {confirmDecision === "approved"
                ? "Confirm Approval"
                : confirmDecision === "declined"
                  ? "Confirm Decline"
                  : "Confirm Request Changes"}
            </div>

            <div className="fp-confirm-message FP_Content">
              {confirmDecision === "approved"
                ? "Are you sure you want to approve this submission?"
                : confirmDecision === "declined"
                  ? "Are you sure you want to decline this submission?"
                  : "Are you sure you want to request changes for this submission?"}
            </div>

            <div className="fp-confirm-actions">
              <button
                type="button"
                className="fp-confirm-cancel FP_Content"
                disabled={isSaving}
                onClick={() => setConfirmDecision(null)}
              >
                Cancel
              </button>

              <button
                type="button"
                className={`fp-confirm-submit FP_Content ${
                  confirmDecision === "declined" ? "is-danger" : ""
                }`}
                disabled={isSaving}
                onClick={() => handleSaveDecision(confirmDecision)}
              >
                {isSaving ? "Saving..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const toJsDate = (value) => {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    const converted = value.toDate();
    return Number.isNaN(converted?.getTime?.()) ? null : converted;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string" || typeof value === "number") {
    const converted = new Date(value);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  if (typeof value?.seconds === "number") {
    const converted = new Date(value.seconds * 1000);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  return null;
};

export const formatSubmittedDate = (dateValue) => {
  const jsDate = toJsDate(dateValue);
  if (!jsDate) return "-";
  return dayjs(jsDate).format("D MMMM YYYY");
};

export const getRiskLabel = (
  submission,
  latestAttemptAiRisk,
  latestAttempt,
) => {
  const rawRisk =
    latestAttemptAiRisk ||
    submission?.riskDetected ||
    submission?.risk ||
    submission?.riskLevel ||
    latestAttempt?.riskDetected ||
    latestAttempt?.risk ||
    latestAttempt?.decision ||
    submission?.latestRisk ||
    "Unknown";

  const normalized = String(rawRisk).toUpperCase();
  if (
    ["HIGH", "MEDIUM", "MED", "LOW", "PROCESSING", "UNKNOWN"].includes(
      normalized,
    )
  ) {
    if (normalized === "MED") return "Medium";
    return normalized.charAt(0) + normalized.slice(1).toLowerCase();
  }

  return "Unknown";
};

export const buildAttachments = (media = [], filesMeta = []) => {
  const list = [];

  for (const item of media) {
    const name = item.originalName || item.fileName || item.name;
    if (!name) continue;
    list.push({
      name,
      mimeType: item.mimeType || item.type || "",
      size: typeof item.size === "number" ? item.size : null,
      downloadURL: item.downloadURL || null,
      storagePath: item.storagePath || null,
    });
  }

  for (const item of filesMeta) {
    const name = item.originalName || item.fileName || item.name;
    if (!name) continue;
    list.push({
      name,
      mimeType: item.mimeType || item.type || "",
      size: typeof item.size === "number" ? item.size : null,
      downloadURL: item.downloadURL || null,
      storagePath: item.storagePath || null,
    });
  }

  const byName = new Map();
  for (const item of list) {
    const key = String(item.name).trim().toLowerCase();
    const existing = byName.get(key);
    if (!existing) {
      byName.set(key, item);
      continue;
    }

    const existingScore = Number(
      Boolean(existing.downloadURL || existing.storagePath),
    );
    const itemScore = Number(Boolean(item.downloadURL || item.storagePath));
    if (itemScore > existingScore) byName.set(key, item);
  }

  return Array.from(byName.values());
};

 export const formatSubmittedDateTime = (dateValue) => {
    const jsDate = toJsDate(dateValue);
    if (!jsDate) return "-";
    return dayjs(jsDate).format("D MMM YYYY, h:mm A");
  };
